<script
    setup
    lang="ts"
    generic="TPackage extends ItineraryPackage = ItineraryPackage"
>
import { TransitionGroup } from 'vue';
// eslint-disable-next-line no-restricted-imports
import ListBanner from '../../ListBanner.vue';
// eslint-disable-next-line no-restricted-imports
import RealTimeLoading from '../../RealTimeLoading.vue';
import LazyMount from '../LazyMount.vue';
import type ItineraryPackage from '@itinerary/ItineraryPackage';
import { BannerSearchResultEnum } from '@clients/ContentApi';

const {
    list = [],
    title,
    horizontalMobile,
    realTimeLoading,
    displayBanners,
    verticalDesktop,
    loadInfinite,
} = defineProps<IProps>();
const emit = defineEmits<{
    viewSection: [];
    loadMore: [];
}>();
const contentStore = useContentStore();
const itineraryStore = useItineraryStore();
const siteConfigs = useSiteConfigs();
interface IProps {
    title?: string;
    list?: TPackage[];
    displayBanners?: boolean;
    horizontalMobile?: boolean;
    realTimeLoading?: boolean;
    verticalDesktop?: boolean;
    loadInfinite?: { hasNext: boolean };
    eager?: boolean;
}
const card = ref<InstanceType<typeof LazyMount>[]>([]);
const cardsList = ref<HTMLDivElement>();
const route = useRoute();
const { isMobile } = useSize();
const isMounted = useMounted();
const viewed = ref(false);
const mouseInCard = ref(false);
const prevCardTop = ref(0);
const prevCard = ref<Element>();
const transitionName = computed(() => {
    if (isMounted.value) {
        return isMobile.value || loading.value ? 'none' : 'list';
    } else {
        return 'list';
    }
});
const component = computed(() => (isMounted.value ? TransitionGroup : 'div'));
const currentModule = useCurrentItineraryModule();
const { currentLoadingStatus, currentAllPackagesLoaded } = moduleToRefs(
    currentModule.value
);
const { loading } = storeToRefs(itineraryStore);
const bannerIndex = computed(() => (list.length >= 4 ? 2 : list.length - 1));
const filteredListBanners = computed(() => {
    if (!siteConfigs.showBanners) {
        return [];
    }
    return contentStore.listBanners
        .filter(b => new RegExp(b.urlPattern || '').test(route.path))
        .filter(
            m =>
                !m.displaySize ||
                m.displaySize === 'both' ||
                (m.displaySize === 'mobile' ? isMobile : !isMobile)
        )
        .filter(banner => {
            switch (banner.searchResult) {
                case BannerSearchResultEnum.Both:
                    return true;
                case BannerSearchResultEnum.Empty:
                    return !list.length;
                case BannerSearchResultEnum.NonEmpty:
                    return !!list.length;
                default:
                    return true;
            }
        })
        .slice(0, 1);
});
const onScroll = () => {
    if (!mouseInCard.value) {
        let index =
            Math.round(
                window.scrollY / card.value?.[0]?.$el.clientHeight || 1
            ) - 1;
        index = index > 0 ? index : 0;
        prevCardTop.value =
            card.value?.[index]?.$el.getBoundingClientRect().top || 0;
        prevCard.value = card.value?.[index]?.$el || null;
    }
};
const mouseenter = (index: number) => {
    prevCardTop.value =
        card.value?.[index]?.$el.getBoundingClientRect().top || 0;
    prevCard.value = card.value?.[index]?.$el || null;
    mouseInCard.value = true;
};
const mouseleave = () => {
    mouseInCard.value = false;
};
const startObserving = () => {
    if (typeof IntersectionObserver === 'undefined') {
        return;
    }
    const observer = new IntersectionObserver(
        entries => {
            if (entries[0]?.isIntersecting) {
                emit('viewSection');
                observer.disconnect();
            }
        },
        {
            threshold: [0, 0.2],
            root: null,
            rootMargin: '0px 0px 0px 0px',
        }
    );
    if (cardsList.value) {
        observer.observe(cardsList.value);
    }
};

function observeVisibility(
    el: Element,
    callback: (
        isVisible: boolean,
        entry: IntersectionObserverEntry,
        observer: IntersectionObserver
    ) => void,
    options?: IntersectionObserverInit
): () => void {
    const observer = new IntersectionObserver(([entry]) => {
        if (entry) {
            callback(entry.isIntersecting, entry, observer);
        }
    }, options);

    observer.observe(el);

    // Return a cleanup function
    return () => observer.disconnect();
}

const clear = ref<() => void | undefined>();

function isFullyVisible(el: Element): boolean {
    const rect = el.getBoundingClientRect();
    return rect.top >= 0 && rect.bottom <= window.innerHeight;
}

const listTransitionTime = computed(() => {
    const baseTime = 200; // minimum time in ms
    const maxTime = 1000; // cap the duration
    const scaledTime = baseTime + Math.sqrt(list.length) * 50;
    return Math.min(scaledTime, maxTime);
});

const scrollToCard = (id: string) => {
    const selectedCard = card.value
        ?.map(item => item.$el)
        .find($el => $el.dataset.itemId === id);
    clear.value?.();
    const intervalCode = setInterval(() => {
        selectedCard?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    }, 200);
    const stop = observeVisibility(selectedCard, (visible, _, observer) => {
        if (visible && isFullyVisible(selectedCard)) {
            nextTick(() => {
                clearInterval(intervalCode);
                observer.disconnect();
            });
        }
    });

    clear.value = () => {
        clearInterval?.(intervalCode);
        stop?.();
    };
};
watch(
    () => list,
    val => {
        if (val.length && !viewed.value) {
            if (typeof IntersectionObserver === 'undefined') {
                emit('viewSection');
            } else {
                startObserving();
            }
            viewed.value = true;
        }
        if (val.length && prevCard.value && !currentAllPackagesLoaded.value) {
            const newCardTop = prevCard.value.getBoundingClientRect().top;
            if (newCardTop > prevCardTop.value) {
                window.scrollTo({
                    top: newCardTop,
                });
            }
        }
    }
);
watch(
    currentAllPackagesLoaded,
    val => {
        if (!process.client) {
            return;
        }
        if (!val) {
            document.addEventListener('scroll', onScroll);
        } else {
            document.removeEventListener('scroll', onScroll);
        }
    },
    { immediate: true }
);
useMrInfiniteScroll({
    children: () => card.value?.map(val => val.root!),
    onLoad: () => emit('loadMore'),
    threshold: 3,
    hasNext: loadInfinite?.hasNext,
    itemsLength: computed(() => list.length),
    isSsr: true,
    disabled: !loadInfinite,
});
onBeforeUnmount(() => document.removeEventListener('scroll', onScroll));
defineExpose({
    scrollToCard,
    clear,
});
</script>

<template>
    <section
        ref="cardsList"
        class="card-section-wrapper"
        :class="{
            'mobile-horizontal': horizontalMobile,
            'desktop-vertical': verticalDesktop,
        }"
    >
        <mr-typography
            v-if="title"
            size="x-large"
            weight="700"
            color="var(--gray-700)"
            class="title"
        >
            {{ title }}
        </mr-typography>
        <ClientOnly>
            <RealTimeLoading
                v-if="realTimeLoading && isMobile"
                margin="12px 0"
            />
        </ClientOnly>
        <component
            :is="component"
            tag="div"
            class="card-section-container"
            :name="transitionName"
            :css="loading"
        >
            <LazyMount
                v-for="(item, index) in list"
                :key="item.key || index"
                ref="card"
                :eager
                class="lazy-mount"
                :style="{
                    order: index,
                    '--transition-time': `${listTransitionTime}ms`,
                }"
                :data-item-id="item.uniqueId"
                :disabled="list.length < 50 || index <= 5"
            >
                <keep-alive>
                    <slot
                        name="card"
                        :item="item!"
                        :index
                        :mouseenter="() => mouseenter(index)"
                        :mouseleave="() => mouseleave()"
                    />
                </keep-alive>
            </LazyMount>
            <template
                v-if="displayBanners && currentLoadingStatus === 'success'"
            >
                <ListBanner
                    v-for="banner in filteredListBanners"
                    :key="banner.id"
                    :banner
                    :style="{ order: bannerIndex }"
                />
            </template>
            <slot name="extra-item" />
        </component>
        <slot />
    </section>
</template>

<style scoped lang="scss">
$breakpoint: 1201px;
.card-section-wrapper {
    @include desktop-only() {
        &.desktop-vertical {
            :deep(.card-section-container) {
                flex-wrap: wrap;
                flex-direction: row;
                gap: 12px;
                justify-content: start !important;
                .lazy-mount {
                    width: auto;
                }
            }
        }
    }
    @include mobile-only($breakpoint) {
        &.desktop-vertical {
            :deep(.card-section-container) {
                justify-content: space-between !important;
            }
        }
    }

    @include mobile-only {
        &.mobile-horizontal {
            overflow-x: scroll;
            @include no-scrollbar;
            max-width: 100vw;
            -webkit-overflow-scrolling: touch;
            .card-section-container {
                flex-direction: row;
                width: fit-content;
                max-width: 100%;
            }

            :deep(.card-section-container) {
                > * {
                    margin: 12px 4px;
                }
            }
        }
    }
}

.card-section-container {
    position: relative;
    display: flex;
    flex-direction: column;
}

:deep(.card-section-container) {
    > * {
        margin: 4px 0 !important;
    }
}

.title {
    margin-block: 12px;
}

.list-move,
.list-enter-active,
.list-leave-active {
    transition: all var(--transition-time) ease;
}

.list-enter-from,
.list-leave-to {
    opacity: 0;
    transform: translateX(30px);
}

.list-leave-active {
    position: absolute;
}
</style>
